import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createFileRoute, useBlocker } from "@tanstack/react-router";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  // ChevronsUpDown,
  Info,
  Loader2,
  MessageSquare,
  Mic,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { HTTPError } from "ky";
import type { PropsWithChildren } from "react";
import type { SettingsTab } from "@/routes/_app/-components/SettingsTabs";
import type { CustomPiiEntity, PiiSeverity } from "@/lib/queries/meetings";
import {
  customPiiQueryOptions,
  featureByLabelQueryOptions,
  standardPiiQueryOptions,
  useUpdateFeatureMutation,
} from "@/lib/queries/meetings";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/http";
import {
  ConfirmationDialog,
  ConfirmationDialogWarningIcon,
  UnsavedChangesDialog,
} from "@/components/ui/confirmation-dialog";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { requiredString } from "@/lib/utils/zod";
import {
  SETTINGS_TABS,
  SettingsTabsList,
} from "@/routes/_app/-components/SettingsTabs";
import { BillingSettings } from "@/routes/_app/-components/BillingSettings";

const settingsSearchSchema = z.object({
  tab: z.enum(SETTINGS_TABS).catch("security"),
  checkout: z.literal("success").optional(),
  portal: z.literal("return").optional(),
});

export const Route = createFileRoute("/_app/settings")({
  component: RouteComponent,
  validateSearch: settingsSearchSchema,
});

const updateBotSettings = z.object({
  default_meeting_bot_name: requiredString(
    "Default meeting agent name required",
  ),
  bot_intro_text: requiredString("Should have at least 1 character"),
});

type UpdateBotSettings = z.infer<typeof updateBotSettings>;

type OrgSettings = {
  id: string;
  organization_id: string;
  default_meeting_bot_name: string;
  ai_content_detection_min_words: number;
  bot_intro_enabled: boolean;
  bot_intro_text: string;
  bot_intro_mode: "TEXT" | "VOICE";
  bot_intro_voice_settings: Record<string, unknown> | null;
};

function orgSettingsQueryOptions() {
  return queryOptions({
    queryKey: ["org-settings", "settings"],
    queryFn: () =>
      api.processing.raw
        .get("api/v1/org-settings/settings")
        .json<OrgSettings>(),
  });
}
interface SettingCardProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  onSave?: () => void;
  isSaving?: boolean;
  isLoading?: boolean;
  saveDisabled?: boolean;
}

function ThresholdSettingCardSkeleton() {
  return (
    <>
      <CardHeader className="pb-2">
        <Skeleton className="h-7 w-56 max-w-full" />
        <Skeleton className="h-5 w-96 max-w-full" />
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex min-h-[88px] items-start justify-between gap-4 rounded-lg border bg-gray-50 p-4 dark:bg-transparent">
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-5 w-52 max-w-full" />
            <Skeleton className="h-5 w-80 max-w-full" />
          </div>
          <Skeleton className="h-6 w-11 shrink-0 rounded-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-4 w-52 max-w-full" />
        </div>
        <div className="flex justify-end">
          <Skeleton className="h-10 w-36" />
        </div>
      </CardContent>
    </>
  );
}

function SettingCard({
  title,
  description,
  children,
  onSave,
  isSaving = false,
  isLoading = false,
  saveDisabled = false,
}: SettingCardProps) {
  return (
    <Card className="gap-2">
      {isLoading ? (
        <ThresholdSettingCardSkeleton />
      ) : (
        <>
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold">{title}</CardTitle>
            <p className="text-sm text-muted-foreground">{description}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {children}
            {onSave && (
              <div className="flex justify-end">
                <Button
                  onClick={onSave}
                  disabled={isSaving || saveDisabled}
                  className="bg-[#DD2590] px-8 text-white hover:bg-[#DD2590]/90"
                >
                  {isSaving ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="size-4 animate-spin" />
                      Saving…
                    </span>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </>
      )}
    </Card>
  );
}

interface ToggleSettingProps {
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function ToggleSetting({
  title,
  description,
  checked,
  onCheckedChange,
}: ToggleSettingProps) {
  return (
    <div className="flex min-h-[88px] items-start justify-between gap-4 rounded-lg border bg-gray-50 p-4 dark:bg-transparent">
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="shrink-0 data-[state=checked]:bg-pink-600 data-[state=unchecked]:bg-gray-300 dark:data-[state=unchecked]:border dark:data-[state=unchecked]:border-gray-500 dark:data-[state=unchecked]:bg-gray-600"
      />
    </div>
  );
}

type PiiItem = {
  id: string;
  name: string;
  severity: PiiSeverity;
  isEnabled: boolean;
};

interface SeverityOptionProps {
  label: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}

function SeverityOption({
  label,
  selected,
  disabled,
  onClick,
}: SeverityOptionProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "inline-flex w-full items-center justify-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors sm:w-auto sm:justify-start",
        disabled && "cursor-not-allowed opacity-50",
        selected
          ? "border-[#FAA7E0] bg-pink-50 text-[#C11574] dark:border-pink-700 dark:bg-pink-950/40 dark:text-pink-300"
          : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700",
      )}
    >
      <span
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-full",
          selected
            ? "bg-[#DD2590]"
            : "border border-gray-300 dark:border-gray-600",
        )}
      >
        {selected && <span className="size-1.5 rounded-full bg-white" />}
      </span>
      {label}
    </button>
  );
}

interface PiiRowProps {
  item: PiiItem;
  disabled: boolean;
  onToggle: (isEnabled: boolean) => void;
  onSeverity: (severity: PiiSeverity) => void;
}

function PiiRow({ item, disabled, onToggle, onSeverity }: PiiRowProps) {
  const controlsDisabled = disabled || !item.isEnabled;
  return (
    <div className="flex flex-col gap-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <Switch
          checked={item.isEnabled}
          disabled={disabled}
          onCheckedChange={onToggle}
          className="shrink-0 data-[state=checked]:bg-pink-600 data-[state=unchecked]:bg-gray-300 dark:data-[state=unchecked]:border dark:data-[state=unchecked]:border-gray-500 dark:data-[state=unchecked]:bg-gray-600"
        />
        <span
          className={cn(
            "truncate text-sm font-medium",
            !item.isEnabled && "text-muted-foreground",
          )}
        >
          {item.name}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0">
        <SeverityOption
          label="Critical"
          selected={item.severity === "CRITICAL"}
          disabled={controlsDisabled}
          onClick={() => onSeverity("CRITICAL")}
        />
        <SeverityOption
          label="Confidential"
          selected={item.severity === "CONFIDENTIAL"}
          disabled={controlsDisabled}
          onClick={() => onSeverity("CONFIDENTIAL")}
        />
      </div>
    </div>
  );
}

function PiiDetectionRowsSkeleton() {
  return (
    <div
      className="space-y-1"
      role="status"
      aria-label="Loading PII detection settings"
    >
      <Skeleton className="mb-2 h-5 w-20" />
      {Array.from({ length: 4 }, (_, index) => (
        <div
          key={index}
          className="flex flex-col gap-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        >
          <div className="flex min-w-0 items-center gap-3">
            <Skeleton className="h-6 w-11 shrink-0 rounded-full" />
            <Skeleton className="h-5 w-40" />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0">
            <Skeleton className="h-8 w-full rounded-full sm:w-24" />
            <Skeleton className="h-8 w-full rounded-full sm:w-32" />
          </div>
        </div>
      ))}
      <div className="flex justify-end pt-5">
        <Skeleton className="h-10 w-36" />
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
}

type PiiOverride = { isEnabled?: boolean; severity?: PiiSeverity };

type SectionSaveActions = {
  save: () => Promise<void>;
  discard: () => void;
};

type SettingsUnsavedChangesContextValue = {
  registerActions: (id: string, actions: SectionSaveActions) => void;
  setSectionDirty: (id: string, dirty: boolean) => void;
  requestNavigation: (action: () => void) => void;
};

const SettingsUnsavedChangesContext =
  createContext<SettingsUnsavedChangesContextValue | null>(null);

function useSettingsUnsavedChanges() {
  const context = useContext(SettingsUnsavedChangesContext);
  if (!context) {
    throw new Error(
      "useSettingsUnsavedChanges must be used within SettingsUnsavedChangesProvider",
    );
  }
  return context;
}

function useRegisterUnsavedSection(
  sectionId: string,
  isDirty: boolean,
  save: () => Promise<void>,
  discard: () => void,
) {
  const { registerActions, setSectionDirty } = useSettingsUnsavedChanges();

  useEffect(() => {
    registerActions(sectionId, { save, discard });
  }, [sectionId, save, discard, registerActions]);

  useEffect(() => {
    setSectionDirty(sectionId, isDirty);
  }, [sectionId, isDirty, setSectionDirty]);
}

function SettingsUnsavedChangesProvider({ children }: PropsWithChildren) {
  const sectionsRef = useRef<Map<string, SectionSaveActions>>(new Map());
  const dirtySectionsRef = useRef<Set<string>>(new Set());
  const [isAnyDirty, setIsAnyDirty] = useState(false);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const [isSavingAndLeaving, setIsSavingAndLeaving] = useState(false);
  const pendingNavigationRef = useRef<(() => void) | null>(null);
  const dismissedBlockerRef = useRef(false);

  const registerActions = useCallback(
    (id: string, actions: SectionSaveActions) => {
      sectionsRef.current.set(id, actions);
    },
    [],
  );

  const setSectionDirty = useCallback((id: string, dirty: boolean) => {
    if (dirty) {
      dirtySectionsRef.current.add(id);
    } else {
      dirtySectionsRef.current.delete(id);
    }
    setIsAnyDirty(dirtySectionsRef.current.size > 0);
  }, []);

  const blocker = useBlocker({
    shouldBlockFn: () => isAnyDirty,
    withResolver: true,
    enableBeforeUnload: isAnyDirty,
  });

  useEffect(() => {
    if (blocker.status !== "blocked") {
      dismissedBlockerRef.current = false;
      return;
    }
    if (!unsavedDialogOpen && !dismissedBlockerRef.current) {
      setUnsavedDialogOpen(true);
    }
  }, [blocker.status, unsavedDialogOpen]);

  const proceedPendingNavigation = useCallback(() => {
    const action = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    if (action) {
      action();
    } else if (blocker.status === "blocked") {
      blocker.proceed();
    }
  }, [blocker]);

  const requestNavigation = useCallback(
    (action: () => void) => {
      if (isAnyDirty) {
        pendingNavigationRef.current = action;
        setUnsavedDialogOpen(true);
      } else {
        action();
      }
    },
    [isAnyDirty],
  );

  const handleKeepEditing = useCallback(() => {
    dismissedBlockerRef.current = true;
    pendingNavigationRef.current = null;
    if (blocker.status === "blocked") {
      blocker.reset();
    }
    setUnsavedDialogOpen(false);
  }, [blocker]);

  const handleDiscardAndLeave = useCallback(() => {
    for (const id of dirtySectionsRef.current) {
      sectionsRef.current.get(id)?.discard();
    }
    dirtySectionsRef.current.clear();
    setIsAnyDirty(false);
    setUnsavedDialogOpen(false);
    proceedPendingNavigation();
  }, [proceedPendingNavigation]);

  const handleSaveAndLeave = useCallback(async () => {
    const dirtyIds = Array.from(dirtySectionsRef.current);
    if (dirtyIds.length === 0) return;

    setIsSavingAndLeaving(true);
    try {
      await Promise.all(
        dirtyIds.map(
          (id) => sectionsRef.current.get(id)?.save() ?? Promise.resolve(),
        ),
      );
      dirtySectionsRef.current.clear();
      setIsAnyDirty(false);
      setUnsavedDialogOpen(false);
      proceedPendingNavigation();
    } catch {
      setUnsavedDialogOpen(false);
      pendingNavigationRef.current = null;
      if (blocker.status === "blocked") {
        blocker.reset();
      }
    } finally {
      setIsSavingAndLeaving(false);
    }
  }, [blocker, proceedPendingNavigation]);

  const contextValue = useMemo(
    () => ({ registerActions, setSectionDirty, requestNavigation }),
    [registerActions, setSectionDirty, requestNavigation],
  );

  return (
    <SettingsUnsavedChangesContext.Provider value={contextValue}>
      {children}
      <UnsavedChangesDialog
        open={unsavedDialogOpen}
        onOpenChange={(open) => {
          if (!open) handleKeepEditing();
        }}
        onKeepEditing={handleKeepEditing}
        onDiscardAndLeave={handleDiscardAndLeave}
        onSaveAndLeave={handleSaveAndLeave}
        isSaving={isSavingAndLeaving}
      />
    </SettingsUnsavedChangesContext.Provider>
  );
}

function PiiDetectionSection() {
  const queryClient = useQueryClient();
  const standardQuery = useQuery(standardPiiQueryOptions());
  const customQuery = useQuery(customPiiQueryOptions());

  const isLoading =
    standardQuery.status === "pending" || customQuery.status === "pending";

  const [masterEnabled, setMasterEnabled] = useState(true);
  const [overrides, setOverrides] = useState<Record<string, PiiOverride>>({});

  const standardData = standardQuery.data ?? [];
  const customData = customQuery.data ?? [];

  const saveMutation = useMutation({
    mutationFn: async () => {
      const standardIds = new Set(standardData.map((s) => s.entity_id));
      const customIds = new Set(customData.map((c) => c.id));

      await Promise.all(
        Object.entries(overrides).map(([id, override]) => {
          const payload: { severity?: PiiSeverity; is_enabled?: boolean } = {};
          if (override.severity !== undefined)
            payload.severity = override.severity;
          if (override.isEnabled !== undefined)
            payload.is_enabled = override.isEnabled;
          if (Object.keys(payload).length === 0) return Promise.resolve();

          if (standardIds.has(id)) {
            return api.processing.raw
              .patch(`api/v1/pii/standard/${id}`, { json: payload })
              .json();
          }
          if (customIds.has(id)) {
            return api.processing.raw
              .patch(`api/v1/pii/custom/${id}`, { json: payload })
              .json();
          }
          return Promise.resolve();
        }),
      );
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["pii", "standard"] });
      queryClient.invalidateQueries({ queryKey: ["pii", "custom"] });
      setOverrides({});
      toast.success("PII settings updated");
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to update PII settings");
    },
  });

  const hasChanges = Object.keys(overrides).length > 0;

  const saveSection = useCallback(
    () => saveMutation.mutateAsync(),
    [saveMutation],
  );
  const discardSection = useCallback(() => setOverrides({}), []);

  useRegisterUnsavedSection(
    "pii-detection",
    hasChanges,
    saveSection,
    discardSection,
  );

  const applyOverride = (item: PiiItem): PiiItem => ({
    ...item,
    ...overrides[item.id],
  });

  const customItems: Array<PiiItem> = customData.map((c) =>
    applyOverride({
      id: c.id,
      name: c.label,
      severity: c.severity,
      isEnabled: c.is_enabled,
    }),
  );

  const standardItems: Array<PiiItem> = standardData.map((s) =>
    applyOverride({
      id: s.entity_id,
      name: s.name,
      severity: s.severity,
      isEnabled: s.is_enabled,
    }),
  );

  const isEmpty = customItems.length === 0 && standardItems.length === 0;

  const setOverride = (id: string, value: PiiOverride) => {
    setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...value } }));
  };

  const renderRows = (items: Array<PiiItem>) =>
    items.map((item) => (
      <PiiRow
        key={item.id}
        item={item}
        disabled={!masterEnabled}
        onToggle={(isEnabled) => setOverride(item.id, { isEnabled })}
        onSeverity={(severity) => setOverride(item.id, { severity })}
      />
    ));

  return (
    <Card className="gap-2">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-xl font-bold">
              Confidential Information (PII) Detection
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Configure which sensitive type entities should be detected &amp;
              assign severity.
            </p>
          </div>
          <Switch
            checked={masterEnabled}
            onCheckedChange={setMasterEnabled}
            className="mt-1 shrink-0 data-[state=checked]:bg-pink-600 data-[state=unchecked]:bg-gray-300 dark:data-[state=unchecked]:border dark:data-[state=unchecked]:border-gray-500 dark:data-[state=unchecked]:bg-gray-600"
          />
        </div>
      </CardHeader>
      {masterEnabled && (
        <CardContent>
          {isLoading ? (
            <PiiDetectionRowsSkeleton />
          ) : (
            <div className="space-y-6">
              {customItems.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-muted-foreground">
                    Custom
                  </p>
                  <div>{renderRows(customItems)}</div>
                </div>
              )}

              {standardItems.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-muted-foreground">
                    Standard
                  </p>
                  <div>{renderRows(standardItems)}</div>
                </div>
              )}

              {isEmpty && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No PII entities found.
                </p>
              )}
            </div>
          )}
          {!isLoading && !isEmpty && (
            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !hasChanges}
                className="bg-[#DD2590] px-8 text-white hover:bg-[#DD2590]/90"
              >
                {saveMutation.isPending ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    Saving…
                  </span>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function CustomPiiSeverityBadge({ severity }: { severity: PiiSeverity }) {
  return (
    <Badge
      className={cn(
        "rounded-full",
        severity === "CRITICAL"
          ? "border-[#FAA7E0] bg-pink-50 text-[#C11574] dark:border-pink-700 dark:bg-pink-950/40 dark:text-pink-300"
          : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400",
      )}
    >
      {severity === "CRITICAL" ? "Critical" : "Confidential"}
    </Badge>
  );
}

const customPiiFormSchema = z.object({
  label: requiredString("PII label is required"),
  text: requiredString("Detection value is required"),
  replacement_text: requiredString("Replacement value is required"),
  severity: z.enum(["CRITICAL", "CONFIDENTIAL"]),
});

type CustomPiiFormValues = z.infer<typeof customPiiFormSchema>;

interface SeverityRadioProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

function SeverityRadio({ label, selected, onClick }: SeverityRadioProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors",
        selected
          ? "border-[#FAA7E0] bg-pink-50 text-[#C11574] dark:border-pink-700 dark:bg-pink-950/40 dark:text-pink-300"
          : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700",
      )}
    >
      <span
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-full",
          selected
            ? "bg-[#DD2590]"
            : "border border-gray-300 dark:border-gray-600",
        )}
      >
        {selected && <span className="size-1.5 rounded-full bg-white" />}
      </span>
      {label}
    </button>
  );
}

interface CustomPiiDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity?: CustomPiiEntity | null;
}

function CustomPiiDialog({ open, onOpenChange, entity }: CustomPiiDialogProps) {
  const queryClient = useQueryClient();
  const isEditing = Boolean(entity);

  const form = useForm<CustomPiiFormValues>({
    resolver: zodResolver(customPiiFormSchema),
    defaultValues: {
      label: "",
      text: "",
      replacement_text: "",
      severity: "CRITICAL",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        label: entity?.label ?? "",
        text: entity?.text ?? "",
        replacement_text: entity?.replacement_text ?? "",
        severity: entity?.severity ?? "CRITICAL",
      });
    }
  }, [open, entity, form]);

  const handleError = async (error: unknown, fallback: string) => {
    if (error instanceof HTTPError) {
      try {
        const body = await error.response.json();
        if (body.detail) {
          toast.error(body.detail);
          return;
        }
      } catch {
        // ignore json parse errors
      }
    }
    toast.error(fallback);
  };

  const createMutation = useMutation({
    mutationFn: (values: CustomPiiFormValues) =>
      api.processing.raw
        .post("api/v1/pii/custom/", {
          json: { ...values, is_enabled: true },
        })
        .json<CustomPiiEntity>(),
    onSuccess(created) {
      queryClient.setQueryData<Array<CustomPiiEntity>>(
        ["pii", "custom"],
        (prev) => [...(prev ?? []), created],
      );
      toast.success("Custom PII entity created");
      onOpenChange(false);
    },
    onError: (error) =>
      handleError(error, "Failed to create custom PII entity"),
  });

  const updateMutation = useMutation({
    mutationFn: (values: CustomPiiFormValues) =>
      api.processing.raw
        .patch(`api/v1/pii/custom/${entity?.id}`, {
          json: values,
        })
        .json<CustomPiiEntity>(),
    onSuccess(updated) {
      queryClient.setQueryData<Array<CustomPiiEntity>>(
        ["pii", "custom"],
        (prev) =>
          (prev ?? []).map((item) => (item.id === updated.id ? updated : item)),
      );
      toast.success("Custom PII entity updated");
      onOpenChange(false);
    },
    onError: (error) =>
      handleError(error, "Failed to update custom PII entity"),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      form.reset();
    }
    onOpenChange(next);
  };

  const onSubmit = (values: CustomPiiFormValues) => {
    if (isEditing) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {isEditing ? "Edit Custom PII Entity" : "Add Custom PII Entity"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <p className="font-semibold">PII Entity 1</p>
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm text-muted-foreground">
                    PII Label <span className="text-pink-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="First Name"
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm text-muted-foreground">
                    What to Detect <span className="text-pink-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Andrew"
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="replacement_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm text-muted-foreground">
                    Replacement Value <span className="text-pink-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="PERSON"
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="severity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm text-muted-foreground">
                    Severity <span className="text-pink-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap gap-3">
                      <SeverityRadio
                        label="Critical"
                        selected={field.value === "CRITICAL"}
                        onClick={() => field.onChange("CRITICAL")}
                      />
                      <SeverityRadio
                        label="Confidential"
                        selected={field.value === "CONFIDENTIAL"}
                        onClick={() => field.onChange("CONFIDENTIAL")}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="gap-2 pt-2">
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isPending}
                  className="dark:text-white dark:hover:text-white"
                >
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={isPending}
                className="bg-[#DD2590] px-6 text-white hover:bg-[#DD2590]/90"
              >
                {isPending ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    {isEditing ? "Saving…" : "Adding…"}
                  </span>
                ) : isEditing ? (
                  "Save"
                ) : (
                  "Add"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

interface CustomPiiDeleteDialogProps {
  entity: CustomPiiEntity | null;
  onOpenChange: (open: boolean) => void;
}

function CustomPiiDeleteDialog({
  entity,
  onOpenChange,
}: CustomPiiDeleteDialogProps) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      api.processing.raw.delete(`api/v1/pii/custom/${id}`),
    onSuccess(_data, id) {
      queryClient.setQueryData<Array<CustomPiiEntity>>(
        ["pii", "custom"],
        (prev) => (prev ?? []).filter((item) => item.id !== id),
      );
      toast.success("Custom PII entity deleted");
      onOpenChange(false);
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to delete custom PII entity");
    },
  });

  return (
    <ConfirmationDialog
      open={Boolean(entity)}
      onOpenChange={(open) => {
        if (!open && !deleteMutation.isPending) onOpenChange(false);
      }}
      title="Delete Custom PII Entity"
      description={
        entity
          ? `Are you sure you want to delete "${entity.label}"? This action cannot be undone.`
          : ""
      }
      onConfirm={() => {
        if (entity) deleteMutation.mutate(entity.id);
      }}
      confirmText="Delete"
      cancelText="Keep"
      variant="destructive"
      isLoading={deleteMutation.isPending}
      icon={ConfirmationDialogWarningIcon}
      backgroundDecoration
      emphasizeCancel
    />
  );
}

function CustomPiiTableSkeleton() {
  return (
    <div
      className="overflow-x-auto rounded-lg border"
      role="status"
      aria-label="Loading custom PII entities"
    >
      <div className="min-w-[760px]">
        <div className="grid grid-cols-[1fr_1.3fr_1.3fr_0.8fr_0.4fr] gap-4 bg-muted/50 px-4 py-3">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton
              key={index}
              className={index === 4 ? "ml-auto h-5 w-14" : "h-5 w-24"}
            />
          ))}
        </div>
        {Array.from({ length: 3 }, (_, index) => (
          <div
            key={index}
            className="grid min-h-14 grid-cols-[1fr_1.3fr_1.3fr_0.8fr_0.4fr] items-center gap-4 border-t px-4 py-3"
          >
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-6 w-24 rounded-full" />
            <Skeleton className="ml-auto size-9" />
          </div>
        ))}
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
}

function CustomPiiSection() {
  const customQuery = useQuery(customPiiQueryOptions());
  const customEntities = customQuery.data ?? [];
  const isEmpty = customEntities.length === 0;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<CustomPiiEntity | null>(
    null,
  );
  const [deletingEntity, setDeletingEntity] = useState<CustomPiiEntity | null>(
    null,
  );

  const openCreateDialog = () => {
    setEditingEntity(null);
    setDialogOpen(true);
  };

  const openEditDialog = (entity: CustomPiiEntity) => {
    setEditingEntity(entity);
    setDialogOpen(true);
  };

  return (
    <Card className="gap-2">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-xl font-bold">
              Custom PII Entities
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Create and assign custom entities to be detected during the
              meeting.
            </p>
          </div>
          {!isEmpty && (
            <Button
              onClick={openCreateDialog}
              className="shrink-0 bg-[#DD2590] text-white hover:bg-[#DD2590]/90"
            >
              <Plus className="size-4" />
              New Custom PII
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {customQuery.isLoading ? (
          <CustomPiiTableSkeleton />
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
            <p className="text-lg font-semibold">No Custom PII Created yet</p>
            <p className="text-sm text-muted-foreground">
              Create and assign custom entities to be detected during the
              meeting.
            </p>
            <Button
              onClick={openCreateDialog}
              className="mt-2 bg-[#DD2590] text-white hover:bg-[#DD2590]/90"
            >
              <Plus className="size-4" />
              New Custom PII
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="px-4 text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      Label
                      {/* <ChevronsUpDown className="size-3.5" /> */}
                    </span>
                  </TableHead>
                  <TableHead className="px-4 text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      What to Detect
                      {/* <ChevronsUpDown className="size-3.5" /> */}
                    </span>
                  </TableHead>
                  <TableHead className="px-4 text-muted-foreground">
                    Replacement
                  </TableHead>
                  <TableHead className="px-4 text-muted-foreground">
                    Severity
                  </TableHead>
                  <TableHead className="px-4 text-right text-muted-foreground">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customEntities.map((entity) => (
                  <TableRow key={entity.id}>
                    <TableCell className="px-4 py-3 font-medium">
                      {entity.label}
                    </TableCell>
                    <TableCell className="px-4 py-3">{entity.text}</TableCell>
                    <TableCell className="px-4 py-3">
                      <span className="block max-w-[220px] truncate text-muted-foreground">
                        {entity.replacement_text}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <CustomPiiSeverityBadge severity={entity.severity} />
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                            aria-label="Custom PII actions"
                          >
                            <MoreVertical className="h-5 w-5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          sideOffset={4}
                          className="w-[168px] rounded-lg border-border p-1 shadow-lg"
                        >
                          <DropdownMenuItem
                            onSelect={() => openEditDialog(entity)}
                            className={cn(
                              "mx-1.5 flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-[#414651] dark:text-foreground",
                              "focus:bg-muted focus:text-[#414651] data-highlighted:bg-muted data-highlighted:text-[#414651] dark:focus:text-foreground dark:data-highlighted:text-foreground",
                            )}
                          >
                            <Pencil className="h-[18px] w-[18px] shrink-0 stroke-[1.75]" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => setDeletingEntity(entity)}
                            className={cn(
                              "mx-1.5 flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-red-600",
                              "focus:bg-red-50 focus:text-red-600 data-highlighted:bg-red-50 data-highlighted:text-red-600 dark:focus:bg-red-950/40 dark:data-highlighted:bg-red-950/40",
                            )}
                          >
                            <Trash2 className="h-[18px] w-[18px] shrink-0 stroke-[1.75]" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
      <CustomPiiDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entity={editingEntity}
      />
      <CustomPiiDeleteDialog
        entity={deletingEntity}
        onOpenChange={(open) => {
          if (!open) setDeletingEntity(null);
        }}
      />
    </Card>
  );
}

function ThresholdFeatureSection({ featureLabel }: { featureLabel: string }) {
  const { data: feature, isLoading } = useQuery(
    featureByLabelQueryOptions(featureLabel),
  );
  const updateFeatureMutation = useUpdateFeatureMutation();

  const serverThreshold =
    (feature?.settings.threshold as number | undefined) ?? 0.5;
  const [localThreshold, setLocalThreshold] = useState<string | null>(null);
  const [localIsActive, setLocalIsActive] = useState<boolean | null>(null);

  const serverThresholdValue = String(Math.round(serverThreshold * 100));
  const currentThreshold = localThreshold ?? serverThresholdValue;
  const currentIsActive = localIsActive ?? feature?.is_active ?? true;

  const hasChanges =
    currentThreshold !== serverThresholdValue ||
    currentIsActive !== (feature?.is_active ?? true);

  const discardSection = useCallback(() => {
    setLocalThreshold(null);
    setLocalIsActive(null);
  }, []);

  const saveSection = useCallback(async () => {
    if (!feature) return;
    await updateFeatureMutation.mutateAsync({
      featureId: feature.id,
      payload: {
        is_active: currentIsActive,
        settings: {
          ...feature.settings,
          threshold: Number(currentThreshold) / 100,
        },
      },
    });
  }, [feature, currentIsActive, currentThreshold, updateFeatureMutation]);

  useRegisterUnsavedSection(
    `feature-${featureLabel}`,
    hasChanges,
    saveSection,
    discardSection,
  );

  const handleSave = () => {
    void saveSection();
  };

  return (
    <SettingCard
      title={feature?.name}
      description={feature?.description}
      onSave={handleSave}
      isSaving={updateFeatureMutation.isPending}
      isLoading={isLoading}
      saveDisabled={!hasChanges}
    >
      <ToggleSetting
        title={feature?.name ?? ""}
        description={feature?.description ?? ""}
        checked={currentIsActive}
        onCheckedChange={(checked) => setLocalIsActive(checked)}
      />
      <div className="space-y-2">
        <label className="text-sm font-semibold">Detection Threshold</label>
        <Select
          value={currentThreshold}
          onValueChange={(val) => setLocalThreshold(val)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select threshold" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25% - Low</SelectItem>
            <SelectItem value="50">50% - Medium</SelectItem>
            <SelectItem value="75">75% - High</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Minimum confidence score required
        </p>
      </div>
    </SettingCard>
  );
}

function SecuritySettings() {
  return (
    <div className="space-y-6">
      <PiiDetectionSection />
      <CustomPiiSection />
      <ThresholdFeatureSection featureLabel="voice_authentication" />
      <ThresholdFeatureSection featureLabel="ai_content_detection" />
    </div>
  );
}

// TODO: Uncomment when Notifications tab is ready
// function RealTimeAlertsSection() {
//   const [criticalPiiDetection, setCriticalPiiDetection] = useState(true);
//   const [voiceAuthWarnings, setVoiceAuthWarnings] = useState(true);
//   const [highAiContent, setHighAiContent] = useState(true);
//
//   return (
//     <SettingCard
//       title="Real-time Alerts"
//       description="Configure when and how you receive security alerts"
//       onSave={() => console.log("Saving Real-time Alerts settings")}
//     >
//       <ToggleSetting
//         title="Critical PII Detection"
//         description="Immediate alerts for critical PIIs"
//         checked={criticalPiiDetection}
//         onCheckedChange={setCriticalPiiDetection}
//       />
//       <ToggleSetting
//         title="Voice Authentication Warnings"
//         description="Alert on suspicious voice activity"
//         checked={voiceAuthWarnings}
//         onCheckedChange={setVoiceAuthWarnings}
//       />
//       <ToggleSetting
//         title="High Level of AI Content"
//         description="Alert when AI content exceeds threshold"
//         checked={highAiContent}
//         onCheckedChange={setHighAiContent}
//       />
//     </SettingCard>
//   );
// }
//
// function EmailNotificationsSection() {
//   const [email, setEmail] = useState("admin@acme.com");
//   const [sendSummaries, setSendSummaries] = useState(true);
//   const [sendReports, setSendReports] = useState(true);
//
//   return (
//     <SettingCard
//       title="Email Notifications"
//       description="Meeting summaries and reports"
//       onSave={() => console.log("Saving Email Notifications settings")}
//     >
//       <div className="space-y-2">
//         <label className="text-sm font-semibold">Email Address</label>
//         <input
//           type="email"
//           value={email}
//           onChange={(e) => setEmail(e.target.value)}
//           className="w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm focus:border-pink-500 focus:ring-1 focus:ring-pink-500 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
//           placeholder="Enter your email"
//         />
//       </div>
//       <ToggleSetting
//         title="Send Meeting Summaries"
//         description="Receive daily summaries after the meeting"
//         checked={sendSummaries}
//         onCheckedChange={setSendSummaries}
//       />
//       <ToggleSetting
//         title="Send Meeting Reports"
//         description="Receive daily analytics reports on all your meetings"
//         checked={sendReports}
//         onCheckedChange={setSendReports}
//       />
//     </SettingCard>
//   );
// }
//
// function NotificationsSettings() {
//   return (
//     <div className="space-y-6">
//       <RealTimeAlertsSection />
//       <EmailNotificationsSection />
//     </div>
//   );
// }

// TODO: Uncomment when Calendar tab is ready
// interface ConnectedAccount {
//   id: string;
//   name: string;
//   email: string;
//   provider: "gmail" | "outlook";
// }
//
// interface KeywordTagProps {
//   label: string;
//   selected: boolean;
//   onToggle: () => void;
// }
//
// function KeywordTag({ label, selected, onToggle }: KeywordTagProps) {
//   return (
//     <button
//       type="button"
//       onClick={onToggle}
//       className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
//         selected
//           ? "border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-300 dark:hover:bg-sky-900"
//           : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
//       }`}
//     >
//       {label}
//       {selected ? <X className="size-3.5" /> : <Plus className="size-3.5" />}
//     </button>
//   );
// }
//
// function CalendarIntegrationSection() {
//   const [newEmail, setNewEmail] = useState("");
//   const [accounts, setAccounts] = useState<Array<ConnectedAccount>>([
//     {
//       id: "1",
//       name: "Alan Walker",
//       email: "alan@gmail.com",
//       provider: "gmail",
//     },
//     {
//       id: "2",
//       name: "Alan Walker",
//       email: "alan.walker@outlook.com",
//       provider: "outlook",
//     },
//   ]);
//
//   const handleConnect = () => {
//     if (newEmail) {
//       const provider = newEmail.includes("gmail") ? "gmail" : "outlook";
//       setAccounts([
//         ...accounts,
//         {
//           id: Date.now().toString(),
//           name: "New User",
//           email: newEmail,
//           provider,
//         },
//       ]);
//       setNewEmail("");
//     }
//   };
//
//   const handleDelete = (id: string) => {
//     setAccounts(accounts.filter((acc) => acc.id !== id));
//   };
//
//   return (
//     <SettingCard
//       title="Calendar Integration"
//       description="Connect your calendar to automatically monitor meetings"
//       onSave={() => console.log("Saving Calendar Integration settings")}
//     >
//       <div className="space-y-2">
//         <label className="text-sm font-semibold">Add a New Calendar</label>
//         <div className="flex gap-2">
//           <Input
//             type="email"
//             value={newEmail}
//             onChange={(e) => setNewEmail(e.target.value)}
//             placeholder="Enter your email..."
//             className="flex-1"
//           />
//           <Button variant="outline" onClick={handleConnect}>
//             Connect
//           </Button>
//         </div>
//       </div>
//
//       <div className="space-y-3">
//         <label className="text-sm font-semibold">Added Accounts</label>
//         <div className="space-y-3">
//           {accounts.map((account) => (
//             <div
//               key={account.id}
//               className="flex flex-col gap-3 rounded-lg border bg-white p-4 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700 dark:bg-gray-800"
//             >
//               <div className="flex min-w-0 flex-1 items-center gap-3">
//                 <div className="shrink-0">
//                   {account.provider === "gmail" ? (
//                     <GmailIcon />
//                   ) : (
//                     <OutlookIcon />
//                   )}
//                 </div>
//                 <div className="min-w-0 flex-1">
//                   <p className="truncate font-semibold text-gray-900 dark:text-gray-100">
//                     {account.name}
//                   </p>
//                   <p className="truncate text-sm text-gray-500 dark:text-gray-400">
//                     {account.email}
//                   </p>
//                 </div>
//               </div>
//               <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
//                 <button
//                   type="button"
//                   className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
//                 >
//                   <Pencil className="size-5" />
//                 </button>
//                 <button
//                   type="button"
//                   onClick={() => handleDelete(account.id)}
//                   className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
//                 >
//                   <Trash2 className="size-5" />
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </SettingCard>
//   );
// }
//
// const allKeywords = [
//   "Financial",
//   "Confidential",
//   "Private",
//   "Engineering",
//   "Marketing",
//   "Compliance",
//   "Board",
//   "Kick-off",
// ];
//
// function MeetingFiltersSection() {
//   const [joinFromCalendar, setJoinFromCalendar] = useState(true);
//   const [externalMeetings, setExternalMeetings] = useState(true);
//   const [internalMeetings, setInternalMeetings] = useState(true);
//   const [keywordInput, setKeywordInput] = useState("");
//   const [selectedKeywords, setSelectedKeywords] = useState<Array<string>>([
//     "Financial",
//     "Confidential",
//     "Private",
//     "Engineering",
//     "Marketing",
//     "Compliance",
//     "Board",
//     "Kick-off",
//   ]);
//
//   const handleAddKeyword = (e: React.KeyboardEvent<HTMLInputElement>) => {
//     if (e.key === "Enter" && keywordInput.trim()) {
//       if (!selectedKeywords.includes(keywordInput.trim())) {
//         setSelectedKeywords([...selectedKeywords, keywordInput.trim()]);
//       }
//       setKeywordInput("");
//     }
//   };
//
//   const toggleKeyword = (keyword: string) => {
//     setSelectedKeywords((prev) =>
//       prev.includes(keyword)
//         ? prev.filter((k) => k !== keyword)
//         : [...prev, keyword],
//     );
//   };
//
//   return (
//     <SettingCard
//       title="Meeting Filters"
//       description="Automatically select which meetings to monitor"
//       onSave={() => console.log("Saving Meeting Filters settings")}
//     >
//       <ToggleSetting
//         title="Join Meetings from Calendar"
//         description="Automatically invite the bot to all meetings scheduled in Calendar"
//         checked={joinFromCalendar}
//         onCheckedChange={setJoinFromCalendar}
//       />
//
//       <div className="space-y-3">
//         <ToggleSetting
//           title="External Meetings"
//           description="Monitor calls with external participants"
//           checked={externalMeetings}
//           onCheckedChange={setExternalMeetings}
//         />
//         <ToggleSetting
//           title="Internal Meetings"
//           description="Monitor internal team meetings"
//           checked={internalMeetings}
//           onCheckedChange={setInternalMeetings}
//         />
//       </div>
//
//       <div className="space-y-3">
//         <label className="text-sm font-semibold">Keywords to Monitor</label>
//         <Input
//           type="text"
//           value={keywordInput}
//           onChange={(e) => setKeywordInput(e.target.value)}
//           onKeyDown={handleAddKeyword}
//           placeholder="Start typing..."
//         />
//         <p className="text-xs text-muted-foreground">
//           List of keywords in meeting titles
//         </p>
//         <div className="flex flex-wrap gap-2">
//           {allKeywords.map((keyword) => (
//             <KeywordTag
//               key={keyword}
//               label={keyword}
//               selected={selectedKeywords.includes(keyword)}
//               onToggle={() => toggleKeyword(keyword)}
//             />
//           ))}
//         </div>
//       </div>
//     </SettingCard>
//   );
// }
//
// function CalendarSettings() {
//   return (
//     <div className="space-y-6">
//       <CalendarIntegrationSection />
//       <MeetingFiltersSection />
//     </div>
//   );
// }

const DEFAULT_ANNOUNCEMENT_MESSAGE =
  "Hello, I'm the Polygraf Meeting Guard. This meeting is being recorded for compliance and security purposes. By continuing to participate, you consent to the recording. Please contact your administrator if you have any questions.";

function MeetingBotSettingsSkeleton() {
  return (
    <div
      className="space-y-6 py-6"
      role="status"
      aria-label="Loading agent settings"
    >
      <Card>
        <CardHeader>
          <Skeleton className="h-7 w-52 max-w-full" />
          <Skeleton className="h-5 w-full max-w-xl" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Skeleton className="h-7 w-60 max-w-full" />
            <Skeleton className="h-6 w-28 rounded-full" />
          </div>
          <Skeleton className="h-5 w-full max-w-2xl" />
          <CardAction>
            <Skeleton className="h-6 w-11 rounded-full" />
          </CardAction>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/50">
            <Skeleton className="size-5 shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-11/12" />
              <Skeleton className="h-5 w-4/5" />
            </div>
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-44" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {Array.from({ length: 2 }, (_, index) => (
                <div
                  key={index}
                  className="flex min-h-[88px] items-start gap-3 rounded-lg border-2 p-4"
                >
                  <Skeleton className="size-5 shrink-0" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-5 w-52 max-w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-5 w-16" />
            </div>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-5 w-full max-w-lg" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Skeleton className="h-10 w-36" />
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  );
}

function MeetingBotNameSettings() {
  const orgSettingsQuery = useQuery(orgSettingsQueryOptions());
  const queryClient = useQueryClient();

  const orgSettings = orgSettingsQuery.data;

  const form = useForm<UpdateBotSettings>({
    resolver: zodResolver(updateBotSettings),
    defaultValues: {
      default_meeting_bot_name: orgSettings?.default_meeting_bot_name ?? "",
      bot_intro_text:
        orgSettings?.bot_intro_text || DEFAULT_ANNOUNCEMENT_MESSAGE,
    },
  });
  const [hydratedSettingsId, setHydratedSettingsId] = useState<string | null>(
    orgSettings?.id ?? null,
  );

  useEffect(() => {
    if (!orgSettings || hydratedSettingsId === orgSettings.id) return;

    form.reset({
      default_meeting_bot_name: orgSettings.default_meeting_bot_name,
      bot_intro_text:
        orgSettings.bot_intro_text || DEFAULT_ANNOUNCEMENT_MESSAGE,
    });
    setHydratedSettingsId(orgSettings.id);
  }, [form, hydratedSettingsId, orgSettings]);

  const [localAnnouncementEnabled, setLocalAnnouncementEnabled] = useState<
    boolean | null
  >(null);
  const [localAnnouncementMethod, setLocalAnnouncementMethod] = useState<
    "TEXT" | "VOICE" | null
  >(null);

  const announcementEnabled =
    localAnnouncementEnabled ?? orgSettings?.bot_intro_enabled ?? true;
  const announcementMethod =
    localAnnouncementMethod ?? orgSettings?.bot_intro_mode ?? "VOICE";
  const announcementMessage =
    (form.watch("bot_intro_text") as string | undefined) ?? "";

  const serverAnnouncementEnabled = orgSettings?.bot_intro_enabled ?? true;
  const serverAnnouncementMethod = orgSettings?.bot_intro_mode ?? "VOICE";
  const hasAnnouncementChanges =
    (localAnnouncementEnabled !== null &&
      localAnnouncementEnabled !== serverAnnouncementEnabled) ||
    (localAnnouncementMethod !== null &&
      localAnnouncementMethod !== serverAnnouncementMethod);
  const hasChanges = form.formState.isDirty || hasAnnouncementChanges;

  const updateSettings = useMutation({
    mutationFn: (data: Partial<OrgSettings>) => {
      return api.processing.raw
        .patch(`api/v1/org-settings/settings`, {
          json: data,
        })
        .json<OrgSettings>();
    },
    onSuccess(data) {
      queryClient.setQueryData(["org-settings", "settings"], data);
      toast.success("Settings updated successfully");
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to update settings");
    },
  });

  const discardSection = useCallback(() => {
    if (!orgSettings) return;
    form.reset({
      default_meeting_bot_name: orgSettings.default_meeting_bot_name,
      bot_intro_text:
        orgSettings.bot_intro_text || DEFAULT_ANNOUNCEMENT_MESSAGE,
    });
    setLocalAnnouncementEnabled(null);
    setLocalAnnouncementMethod(null);
  }, [form, orgSettings]);

  const saveSection = useCallback(async () => {
    // Rejecting here keeps the unsaved-changes dialog from saving invalid input:
    // the caller treats a rejection as "stay on the page with the errors shown".
    if (!(await form.trigger())) {
      throw new Error("Bot settings are invalid");
    }
    const values = updateBotSettings.parse(form.getValues());
    await updateSettings.mutateAsync({
      default_meeting_bot_name: values.default_meeting_bot_name,
      bot_intro_enabled: announcementEnabled,
      bot_intro_text: values.bot_intro_text,
      bot_intro_mode: announcementMethod,
    });
    form.reset(values);
    setLocalAnnouncementEnabled(null);
    setLocalAnnouncementMethod(null);
  }, [form, announcementEnabled, announcementMethod, updateSettings]);

  useRegisterUnsavedSection(
    "bot-settings",
    hasChanges,
    saveSection,
    discardSection,
  );

  if (
    orgSettingsQuery.isLoading ||
    (orgSettings && hydratedSettingsId !== orgSettings.id)
  )
    return <MeetingBotSettingsSkeleton />;

  if (orgSettingsQuery.isError) {
    return <div className="p-6 text-red-500">Failed to load settings.</div>;
  }

  function onSubmit(_values: UpdateBotSettings) {
    void saveSection();
  }

  return (
    <div className="space-y-6 py-6">
      {!orgSettings ? (
        <div>No settings found.</div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl font-bold">
                  Meeting Agent Name
                </CardTitle>
                <CardDescription>
                  Update the agent name - this will be applied to all future
                  meetings.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="default_meeting_bot_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter meeting agent name"
                          disabled={updateSettings.isPending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-xl font-bold">
                    Recording announcement
                  </CardTitle>
                  <Badge className="rounded-full border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400">
                    Required by law
                  </Badge>
                </div>
                <CardDescription>
                  Announce to participants that the meeting is being recorded
                  when the bot joins.
                </CardDescription>
                <CardAction>
                  <Switch
                    checked={announcementEnabled}
                    onCheckedChange={setLocalAnnouncementEnabled}
                    className="data-[state=checked]:bg-pink-600"
                  />
                </CardAction>
              </CardHeader>

              {announcementEnabled && (
                <CardContent className="space-y-6">
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/50">
                    <div className="flex gap-3">
                      <TriangleAlert className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
                      <p className="text-sm text-amber-800 dark:text-amber-300">
                        <span className="font-semibold">Why this matters:</span>{" "}
                        Laws like the GDPR, CCPA, and wiretapping statutes in
                        many US states require all-party consent before
                        recording a conversation. Announcing the recording at
                        the start of a meeting satisfies this requirement and
                        protects your organization from liability.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                      Announcement Method
                    </label>
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setLocalAnnouncementMethod("VOICE")}
                        className={cn(
                          "flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-colors",
                          announcementMethod === "VOICE"
                            ? "border-pink-600 bg-pink-50 dark:border-pink-500 dark:bg-pink-950/20"
                            : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700",
                        )}
                      >
                        <Mic
                          className={cn(
                            "mt-0.5 size-5 shrink-0",
                            announcementMethod === "VOICE"
                              ? "text-pink-600 dark:text-pink-400"
                              : "text-muted-foreground",
                          )}
                        />
                        <div>
                          <p className="font-semibold">Voice message</p>
                          <p className="text-sm text-muted-foreground">
                            Bot speaks aloud when joining
                          </p>
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setLocalAnnouncementMethod("TEXT")}
                        className={cn(
                          "flex items-start gap-3 rounded-lg border-2 p-4 text-left transition-colors",
                          announcementMethod === "TEXT"
                            ? "border-pink-600 bg-pink-50 dark:border-pink-500 dark:bg-pink-950/20"
                            : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700",
                        )}
                      >
                        <MessageSquare
                          className={cn(
                            "mt-0.5 size-5 shrink-0",
                            announcementMethod === "TEXT"
                              ? "text-pink-600 dark:text-pink-400"
                              : "text-muted-foreground",
                          )}
                        />
                        <div>
                          <p className="font-semibold">Chat message</p>
                          <p className="text-sm text-muted-foreground">
                            Bot posts a message in meeting chat
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                        Announcement Message
                      </label>
                      <span className="text-sm text-muted-foreground">
                        {announcementMessage.length} / 500
                      </span>
                    </div>
                    <FormField
                      control={form.control}
                      name="bot_intro_text"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              {...field}
                              maxLength={500}
                              placeholder="Enter announcement message..."
                              className="min-h-24 resize-y"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <p className="text-sm text-muted-foreground">
                      This message will be delivered immediately when the bot
                      joins the meeting.
                    </p>
                  </div>
                </CardContent>
              )}
            </Card>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={updateSettings.isPending || !hasChanges}
                className="flex bg-[#DD2590] px-8 text-white hover:bg-[#DD2590]/90"
              >
                {updateSettings.isPending ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    Saving…
                  </span>
                ) : (
                  "Save changes"
                )}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
}
function GlobalSettingsNotice() {
  return (
    <div className="flex items-center gap-2 rounded-md border border-[#B9E6FE] bg-[#F0F9FF] px-4 py-3 text-sm text-[#0086C9] dark:border-pink-900 dark:bg-pink-950/40 dark:text-pink-300">
      <Info className="size-4 shrink-0" />
      <span>
        These settings are applied globally to your organization. Changing this
        will reflect on all users.
      </span>
    </div>
  );
}

function SettingsPageContent() {
  const { tab: activeTab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { requestNavigation } = useSettingsUnsavedChanges();

  const handleTabChange = useCallback(
    (value: string) => {
      if (value === activeTab) return;
      if (!SETTINGS_TABS.includes(value as SettingsTab)) return;
      requestNavigation(() => {
        void navigate({ search: { tab: value as SettingsTab }, replace: true });
      });
    },
    [activeTab, navigate, requestNavigation],
  );

  return (
    <div className="w-full min-w-0 space-y-6 p-4 md:p-6">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full min-w-0"
      >
        <SettingsTabsList />

        <TabsContent value="security" className="mt-6 space-y-6">
          <GlobalSettingsNotice />
          <SecuritySettings />
        </TabsContent>

        <TabsContent value="notifications" className="mt-6 space-y-6">
          <GlobalSettingsNotice />
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-lg font-semibold text-muted-foreground">
                Coming soon
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Notification settings will be available here.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="defaults" className="mt-6 space-y-6">
          <GlobalSettingsNotice />
          <MeetingBotNameSettings />
        </TabsContent>

        <TabsContent value="billing" className="mt-6 space-y-6">
          <BillingSettings />
        </TabsContent>

        {/* <TabsContent value="calendar" className="mt-6">
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
            <p className="text-lg font-semibold text-muted-foreground">
              Coming soon
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Calendar settings will be available here.
            </p>
          </div>
        </TabsContent> */}
      </Tabs>
    </div>
  );
}

function RouteComponent() {
  return (
    <SettingsUnsavedChangesProvider>
      <SettingsPageContent />
    </SettingsUnsavedChangesProvider>
  );
}
